// Cache to avoid repeated API calls
const cache = new Map();
const CACHE_DURATION = 30 * 60 * 1000; // 30 minutes

const WMO_SEVERE = [95, 96, 97, 99]; // Thunderstorm
const WMO_HEAVY_RAIN = [65, 67, 82];
const WMO_HEAVY_SNOW = [75, 77, 86];

export async function fetchWeather(lat, lng) {
  const key = `${lat},${lng}`;
  const cached = cache.get(key);
  if (cached && Date.now() - cached.time < CACHE_DURATION) {
    return cached.data;
  }

  const url = `https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lng}&current=temperature_2m,wind_speed_10m,precipitation_probability,weather_code,cloud_cover&timezone=auto`;

  const res = await fetch(url);
  if (!res.ok) throw new Error("Weather fetch failed");
  const json = await res.json();
  const c = json.current;

  const data = {
    temperature: Math.round(c.temperature_2m),
    windSpeed: Math.round(c.wind_speed_10m),
    rainProbability: c.precipitation_probability ?? 0,
    cloudCover: c.cloud_cover ?? 0,
    weatherCode: c.weather_code,
    fetchedAt: new Date().toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" }),
  };

  cache.set(key, { data, time: Date.now() });
  return data;
}

export async function getVisitRecommendation(place) {
  const lat = place.lat ?? place.latitude;
  const lng = place.lng ?? place.longitude;

  if (!lat || !lng) {
    return { status: "no_data", label: "No Location Data", color: "#5c5f60", reasons: [], weather: null };
  }

  const rules = {
    minTemperature: place.weatherRules?.minTemperature ?? 5,
    maxWindSpeed: place.weatherRules?.maxWindSpeed ?? 25,
    maxRainProbability: place.weatherRules?.maxRainProbability ?? 40,
    winterAccessible: place.weatherRules?.winterAccessible ?? true,
  };

  const weather = await fetchWeather(lat, lng);
  const { temperature, windSpeed, rainProbability, weatherCode } = weather;

  const month = new Date().getMonth() + 1; // 1-12
  const isWinter = month === 12 || month === 1 || month === 2;

  let score = 100;
  const reasons = [];

  // Severe weather — immediate
  if (WMO_SEVERE.includes(weatherCode)) {
    return {
      status: "not_recommended",
      label: "Not Recommended Today",
      color: "#ba1a1a",
      reasons: ["⛈ Thunderstorm or severe weather detected"],
      weather,
    };
  }

  // Heavy rain
  if (WMO_HEAVY_RAIN.includes(weatherCode)) {
    score -= 40;
    reasons.push("🌧 Heavy rain expected");
  }

  // Heavy snow
  if (WMO_HEAVY_SNOW.includes(weatherCode)) {
    score -= 40;
    reasons.push("❄️ Heavy snowfall detected");
  }

  // Temperature
  if (temperature < rules.minTemperature) {
    score -= 25;
    reasons.push(`🌡 Temperature too low (${temperature}°C, min ${rules.minTemperature}°C)`);
  } else {
    reasons.push(`🌡 Temperature is ${temperature}°C`);
  }

  // Wind
  if (windSpeed > rules.maxWindSpeed) {
    score -= 25;
    reasons.push(`💨 High wind speed (${windSpeed} km/h, max ${rules.maxWindSpeed} km/h)`);
  } else {
    reasons.push(`💨 Wind speed is acceptable (${windSpeed} km/h)`);
  }

  // Rain probability
  if (rainProbability > rules.maxRainProbability) {
    score -= 25;
    reasons.push(`🌂 High rain probability (${rainProbability}%)`);
  } else {
    reasons.push(`☂️ Low rain probability (${rainProbability}%)`);
  }

  // Winter access
  if (!rules.winterAccessible && isWinter) {
    score -= 20;
    reasons.push("🏔 This location may have limited winter access");
  }

  // Determine status
  let status, label, color;
  if (score >= 85) {
    status = "excellent";
    label = "Excellent Day to Visit";
    color = "#2d7a4f";
  } else if (score >= 65) {
    status = "good";
    label = "Recommended Today";
    color = "#2d7a4f";
  } else if (score >= 40) {
    status = "caution";
    label = "Visit With Caution";
    color = "#b45309";
  } else {
    status = "not_recommended";
    label = "Not Recommended Today";
    color = "#ba1a1a";
  }

  return { status, label, color, score, reasons, weather };
}
